import { Button, Select } from '@cloudflare/kumo';
import { ReactNode } from 'react';
import { ICONS, Glyph } from '../ui/icons';

export type SortDir = 'asc' | 'desc';
export interface SortState<K extends string> { key: K; dir: SortDir }

/** Cabeçalho ordenável (seta como no Atlas: ▾ ativo, ⇕ inativo) */
export function SortHeader<K extends string>({ label, sortKey, sort, onSort }: { label: ReactNode; sortKey: K; sort: SortState<K>; onSort: (s: SortState<K>) => void }) {
  const active = sort.key === sortKey;
  // O Cloudscape não tem o glifo "Unsorted" do LeafyGreen: um círculo neutro
  // marca "sem ordenação" e as setas marcam a direção.
  const iconName = !active ? ICONS.Unsorted : sort.dir === 'asc' ? ICONS.SortAscending : ICONS.SortDescending;
  // aria-sort só é válido no cabeçalho (th), não no botão: o estado vai em texto para leitores de tela
  const state = !active ? 'sem ordenação' : sort.dir === 'asc' ? 'em ordem crescente' : 'em ordem decrescente';
  return (
    <button
      type="button"
      className={`atlas-sort${active ? ' atlas-sort--active' : ''}`}
      onClick={() => onSort({ key: sortKey, dir: active && sort.dir === 'asc' ? 'desc' : 'asc' })}
    >
      {label}<Glyph icon={iconName} size={16}/>
      <span className="sr-only">, {state}; clique para ordenar</span>
    </button>
  );
}

/** Ordena uma lista pelo valor da chave (texto em pt-BR, números e datas) */
export function sortBy<T, K extends string>(rows: T[], sort: SortState<K>, value: (row: T, key: K) => string | number) {
  const m = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = value(a, sort.key); const y = value(b, sort.key);
    return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), 'pt-BR', { sensitivity: 'base', numeric: true })) * m;
  });
}

export const PAGE_SIZES = [10, 25, 50, 100] as const;

/**
 * Rodapé de paginação no layout do Atlas: "Itens por página [25] · 1–25 de
 * 80 itens · [página] de N ‹ ›".
 */
export function TablePager({ page, pageSize, total, onPage, onPageSize }: { page: number; pageSize: number; total: number; onPage: (p: number) => void; onPageSize: (n: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const first = total ? (page - 1) * pageSize + 1 : 0;
  const last = Math.min(total, page * pageSize);
  const sizeOptions = PAGE_SIZES.map((n) => ({ value: String(n), label: String(n) }));
  const pageOptions = Array.from({ length: pages }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
  return (
    <div className="atlas-pager">
      <div className="atlas-pager__size">
        <span>Itens por página:</span>
        <Select value={(sizeOptions.find((o) => o.value === String(pageSize)) ?? null)?.value ?? null} items={sizeOptions} onValueChange={(value: string | null) => { onPageSize(Number(value ?? '')); onPage(1); }} aria-label="Itens por página" />
      </div>
      <span className="atlas-pager__range">{first}–{last} de {total.toLocaleString('pt-BR')} {total === 1 ? 'item' : 'itens'}</span>
      <div className="atlas-pager__nav">
        <Select value={(pageOptions.find((o) => o.value === String(page)) ?? null)?.value ?? null} items={pageOptions} onValueChange={(value: string | null) => onPage(Number(value ?? ''))} aria-label="Página" />
        <span>de {pages}</span>
        <Button variant="ghost" shape="square" icon={ICONS.ChevronLeft} aria-label="Página anterior" disabled={page <= 1} onClick={() => onPage(page - 1)} />
        <Button variant="ghost" shape="square" icon={ICONS.ChevronRight} aria-label="Próxima página" disabled={page >= pages} onClick={() => onPage(page + 1)} />
      </div>
    </div>
  );
}
